import "./index.css";
import { useCallback, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/store/app/slice";
import { ROUTES } from "@/router";
import { Button, Message, RadioCard } from "@/ui-kit";
import Logo from "@/assets/illustrations/logo.png";
import { AuthState } from "@/types/common";
import { PageSpinner } from "@/components/PageSpinner";
import { type IUser, UserLoadingStatus } from "@/types/users";
import axios from "axios";
import { Environment, getApiUrl, getEnvironment } from "@/utils/getEnvironment";

const TenantSelection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authenticated = useAppStore((s) => s.authenticated);
  const userTenants = useAppStore((s) => s.userTenants);
  const [selectedTenant, setSelectedTenant] = useState<string>("");

  const handleTenantSelect = useCallback((tenant: string) => {
    setSelectedTenant(tenant);
  }, []);

  const handleContinue = useCallback(() => {
    if (!selectedTenant) return;

    try {
      const plainAxios = axios.create({
        baseURL: getApiUrl(),
        withCredentials: getEnvironment() !== Environment.Local,
      });

      useAppStore.getState().setUserLoaded(UserLoadingStatus.Loading);
      plainAxios
        .get<IUser>("/api/auth/currentUser", {
          headers: {
            Authorization: `Bearer ${useAppStore.getState().accessToken}`,
            "X-Tenant-Name": selectedTenant,
          },
        })
        .then(({ data }) => {
          useAppStore.getState().setUser(data);
          useAppStore.getState().setUserLoaded(UserLoadingStatus.Loaded);
          const encodedTenant = encodeURIComponent(selectedTenant);
          navigate(`/${encodedTenant}/${ROUTES.PROJECTS}`);
        });
    } catch (_) {
      useAppStore.getState().setUserLoaded(UserLoadingStatus.Loaded);
    }
  }, [selectedTenant, navigate]);

  if (authenticated === AuthState.Loading) {
    return <PageSpinner />;
  }

  if (authenticated === AuthState.Unauthenticated) {
    return <Navigate to={`/${ROUTES.AUTH}/${ROUTES.SIGN_IN}`} />;
  }

  if (!userTenants || userTenants.length === 0) {
    return (
      <div className="tenant-selection__underlay">
        <div className="tenant-selection">
          <header className="tenant-selection__header">
            <img
              src={Logo}
              alt="Logo"
              className="tenant-selection__header-img"
            />
          </header>
          <div className="tenant-selection__main">
            <div className="tenant-selection__content">
              <h3 className="tenant-selection__title nuco-typography--h3 bold">
                {t("tenant_selection.no_tenants_title")}
              </h3>
              <p className="tenant-selection__subtitle nuco-typography--body regular">
                {t("tenant_selection.no_tenants_subtitle")}
              </p>
              <div className="tenant-selection__message">
                <Message
                  text={t("tenant_selection.no_tenants_message")}
                  variant="yellow"
                />
              </div>
              <div className="tenant-selection__actions">
                <Button
                  size="lg"
                  variant="outlined"
                  content={t("action.logout")}
                  onClick={() => navigate(`/${ROUTES.SIGN_OUT}`)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tenant-selection__underlay">
      <div className="tenant-selection">
        <header className="tenant-selection__header">
          <img src={Logo} alt="Logo" className="tenant-selection__header-img" />
        </header>
        <div className="tenant-selection__main">
          <div className="tenant-selection__content">
            <h3 className="tenant-selection__title nuco-typography--h3 bold">
              {t("tenant_selection.title")}
            </h3>
            <p className="tenant-selection__subtitle nuco-typography--body regular">
              {t("tenant_selection.subtitle")}
            </p>

            <div className="tenant-selection__list">
              {userTenants.map((tenant) => (
                <RadioCard
                  key={tenant}
                  name="tenant"
                  value={tenant}
                  label={tenant}
                  checked={selectedTenant === tenant}
                  onChange={() => handleTenantSelect(tenant)}
                  radioPosition="start"
                  color="layer2"
                />
              ))}
            </div>

            <div className="tenant-selection__actions">
              <Button
                size="lg"
                content={t("action.continue")}
                onClick={handleContinue}
                disabled={!selectedTenant}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantSelection;
